import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Table,
  Alert,
  Spinner,
  ListGroup,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";
import { AsyncPaginate } from "react-select-async-paginate";
import { authFetch } from "../utils/authFetch";
import JurnalTvaIntrariIncasare from "./JurnalTvaIntrariIncasare";
import JurnalTvaValuta from "./JurnalTvaValuta";
import { FaChevronDown, FaChevronRight } from "react-icons/fa";

const LIMIT = 100;

export default function RapoarteIntrari() {
  const navigate = useNavigate();
  const [activeReport, setActiveReport] = useState("furnizori_neplatiti");

  // Date filters
  const today = new Date();
  const yyyy = today.getFullYear();

  const [from, setFrom] = useState(new Date(yyyy, today.getMonth(), 1));
  const [to, setTo] = useState(today);
  const [atDate, setAtDate] = useState(today);
  const [contact, setContact] = useState(null);

  const formatDateString = (date) => {
    if (!date) return "";
    if (typeof date === "string") return date;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  // States for fetching
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [data, setData] = useState({ rows: [], totals_by_cota: [] });
  const [loaded, setLoaded] = useState(false);

  // Accordion state for suppliers invoices list
  const [expandedSuppliers, setExpandedSuppliers] = useState({});

  const fmt2 = (n) =>
    Number(n ?? 0).toLocaleString("ro-RO", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  // Load options for contact picker
  const loadContactOptions = useCallback(async (inputValue, _loaded, additional) => {
    const page = additional?.page ?? 1;
    const q = (inputValue || "").trim();
    const skip = (page - 1) * LIMIT;
    try {
      const res = await authFetch(
        `/contacte/?search=${encodeURIComponent(q)}&skip=${skip}&limit=${LIMIT}`
      );
      const items = Array.isArray(res)
        ? res
        : Array.isArray(res?.items)
          ? res.items
          : [];
      const total =
        typeof res?.total === "number"
          ? res.total
          : typeof res?.count === "number"
            ? res.count
            : undefined;

      const options = items.map((c) => ({
        value: c.id,
        label: `${c.bxdenumire}${c.bxcodfiscal ? ` (${c.bxcodfiscal})` : ""}`,
      }));

      const hasMore =
        typeof total === "number"
          ? skip + items.length < total
          : items.length === LIMIT;

      return {
        options,
        hasMore,
        additional: { page: page + 1 },
      };
    } catch (e) {
      console.error(e);
      return { options: [], hasMore: false, additional: { page } };
    }
  }, []);

  const loadData = async () => {
    setBusy(true);
    setErr("");
    setLoaded(false);
    setData({ rows: [], totals_by_cota: [] });
    try {
      let url = "";
      const contactId = contact?.value;
      const contactQuery = contactId ? `&contact_id=${contactId}` : "";

      const fromStr = formatDateString(from);
      const toStr = formatDateString(to);
      const atStr = formatDateString(atDate);

      if (activeReport === "furnizori_neplatiti") {
        url = `/rapoarte/furnizori-neplatiti?at_date=${atStr}${contactQuery}`;
      } else if (activeReport === "intrari_perioada") {
        url = `/rapoarte/intrari-perioada?from=${fromStr}&to=${toStr}${contactQuery}`;
      } else if (activeReport === "tva_direct") {
        url = `/rapoarte/jurnal-tva-intrari-direct?from=${fromStr}&to=${toStr}`;
      }

      if (url) {
        const res = await authFetch(url);
        if (activeReport === "furnizori_neplatiti" || activeReport === "intrari_perioada") {
          setData({ rows: Array.isArray(res?.rows) ? res.rows : [], totals_by_cota: [] });
        } else {
          setData({
            rows: Array.isArray(res?.rows) ? res.rows : [],
            totals_by_cota: Array.isArray(res?.totals_by_cota) ? res.totals_by_cota : [],
          });
        }
        setLoaded(true);
      }
    } catch (e) {
      setErr(e?.message || "Eroare la încărcare date raport.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    setExpandedSuppliers({});
    setData({ rows: [], totals_by_cota: [] });
    setLoaded(false);
    setErr("");
  }, [activeReport]);

  const groupedSuppliers = useMemo(() => {
    if (activeReport !== "furnizori_neplatiti") return [];
    const map = {};
    for (const r of data.rows || []) {
      const kid = r.contact_id;
      if (!map[kid]) {
        map[kid] = {
          contact_id: r.contact_id,
          contact_name: r.contact_name,
          total_value: 0,
          total_settled: 0,
          total_sold: 0,
          invoices: [],
        };
      }
      map[kid].total_value += r.bxgvalue;
      map[kid].total_settled += r.settled_value;
      map[kid].total_sold += r.bxsold;
      map[kid].invoices.push(r);
    }
    return Object.values(map).sort((a, b) => b.total_sold - a.total_sold);
  }, [data.rows, activeReport]);

  const toggleSupplier = (id) => {
    setExpandedSuppliers((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Grouped VAT invoices for tva_direct report
  const groupedTvaDirect = useMemo(() => {
    if (activeReport !== "tva_direct") return [];
    const m = new Map();
    for (const r of data.rows || []) {
      const k = r.doc_id;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(r);
    }
    const arr = Array.from(m.entries()).map(([doc_id, rows]) => {
      rows.sort((a, b) => Number(a.cota ?? 0) - Number(b.cota ?? 0));
      return { doc_id, rows };
    });

    arr.sort((A, B) => {
      const a0 = A.rows[0] || {};
      const b0 = B.rows[0] || {};
      const da = String(a0.doc_date || "");
      const db = String(b0.doc_date || "");
      if (da !== db) return da.localeCompare(db);
      return String(a0.doc_no || "").localeCompare(String(b0.doc_no || ""));
    });
    return arr;
  }, [data.rows, activeReport]);

  const totalsGeneralDirect = useMemo(() => {
    const t = data.totals_by_cota || [];
    return {
      baza: t.reduce((s, x) => s + Number(x.baza || 0), 0),
      tva: t.reduce((s, x) => s + Number(x.tva || 0), 0),
      total: t.reduce((s, x) => s + Number(x.total || 0), 0),
    };
  }, [data.totals_by_cota]);

  return (
    <div className="p-3">
      <Row className="g-4">
        {/* Left menu column */}
        <Col xs={12} md={3} className="d-print-none">
          <Card className="shadow-sm border-0 rounded-4 overflow-hidden mb-3">
            <Card.Header className="bg-primary text-white py-3">
              <h5 className="fw-bold mb-0">Rapoarte Intrări</h5>
            </Card.Header>
            <ListGroup variant="flush">
              <ListGroup.Item
                action
                active={activeReport === "furnizori_neplatiti"}
                onClick={() => setActiveReport("furnizori_neplatiti")}
                className="py-3 px-4 border-0"
              >
                Furnizori neplătiți la dată
              </ListGroup.Item>
              <ListGroup.Item
                action
                active={activeReport === "intrari_perioada"}
                onClick={() => setActiveReport("intrari_perioada")}
                className="py-3 px-4 border-0"
              >
                Intrări în perioadă
              </ListGroup.Item>
              <ListGroup.Item
                action
                active={activeReport === "tva_direct"}
                onClick={() => setActiveReport("tva_direct")}
                className="py-3 px-4 border-0"
              >
                Jurnal TVA direct taxabile
              </ListGroup.Item>
              <ListGroup.Item
                action
                active={activeReport === "tva_incasare"}
                onClick={() => setActiveReport("tva_incasare")}
                className="py-3 px-4 border-0"
              >
                Jurnal TVA la încasare
              </ListGroup.Item>
              <ListGroup.Item
                action
                active={activeReport === "tva_valuta"}
                onClick={() => setActiveReport("tva_valuta")}
                className="py-3 px-4 border-0"
              >
                Jurnal TVA în valută
              </ListGroup.Item>
            </ListGroup>
          </Card>
        </Col>

        {/* Right content column */}
        <Col xs={12} md={9}>
          {activeReport === "tva_incasare" && <JurnalTvaIntrariIncasare />}
          {activeReport === "tva_valuta" && <JurnalTvaValuta />}

          {activeReport !== "tva_incasare" && activeReport !== "tva_valuta" && (
            <Card className="shadow-sm border-0 rounded-4">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="fw-bold mb-0 text-dark">
                    {activeReport === "furnizori_neplatiti" && "Raport Furnizori Neplătiți la Dată"}
                    {activeReport === "intrari_perioada" && "Raport Intrări în Perioadă"}
                    {activeReport === "tva_direct" && "Jurnal TVA Intrări Direct Taxabile"}
                  </h4>
                  {loaded && (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="d-print-none fw-semibold shadow-sm"
                      onClick={() => window.print()}
                    >
                      <i className="bi bi-printer me-1"></i> Tipărește
                    </Button>
                  )}
                </div>

                {/* Filters */}
                <Form className="bg-light p-3 rounded-4 mb-4 border border-light-subtle d-print-none">
                  <Row className="align-items-end g-3">
                    {activeReport === "furnizori_neplatiti" && (
                      <Col xs={12} md={4}>
                        <Form.Label className="fw-semibold text-secondary small mb-1">
                          Restanțe la data
                        </Form.Label>
                        <DatePicker
                          selected={atDate}
                          onChange={(date) => setAtDate(date)}
                          dateFormat="dd.MM.yyyy"
                          className="form-control border-0 shadow-sm"
                        />
                      </Col>
                    )}

                    {(activeReport === "intrari_perioada" || activeReport === "tva_direct") && (
                      <>
                        <Col xs={12} md={3} className="d-print-none">
                          <Form.Label className="fw-semibold text-secondary small mb-1">
                            De la
                          </Form.Label>
                          <DatePicker
                            selected={from}
                            onChange={(date) => setFrom(date)}
                            dateFormat="dd.MM.yyyy"
                            className="form-control border-0 shadow-sm"
                          />
                        </Col>
                        <Col xs={12} md={3} className="d-print-none">
                          <Form.Label className="fw-semibold text-secondary small mb-1">
                            Până la
                          </Form.Label>
                          <DatePicker
                            selected={to}
                            onChange={(date) => setTo(date)}
                            dateFormat="dd.MM.yyyy"
                            className="form-control border-0 shadow-sm"
                          />
                        </Col>
                      </>
                    )}

                    {(activeReport === "furnizori_neplatiti" || activeReport === "intrari_perioada") && (
                      <Col xs={12} md={5}>
                        <Form.Label className="fw-semibold text-secondary small mb-1">
                          Furnizor (opțional)
                        </Form.Label>
                        <AsyncPaginate
                          value={contact}
                          loadOptions={loadContactOptions}
                          onChange={setContact}
                          isClearable
                          placeholder="Caută furnizor..."
                          className="shadow-sm border-0"
                        />
                      </Col>
                    )}

                    <Col xs={12} md={activeReport === "tva_direct" ? 6 : 3}>
                      <Button
                        onClick={loadData}
                        disabled={busy}
                        className="w-100 fw-semibold shadow-sm"
                      >
                        {busy ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Se încarcă...
                          </>
                        ) : (
                          "Generează Raport"
                        )}
                      </Button>
                    </Col>
                  </Row>
                </Form>

                {err && (
                  <Alert variant="danger" className="rounded-3 shadow-sm mb-4 border-0">
                    {err}
                  </Alert>
                )}

                {/* Report Tables */}
                <div className="table-responsive">
                  {busy ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" variant="primary" />
                      <div className="mt-2 text-muted small">Se calculează raportul...</div>
                    </div>
                  ) : (
                    <>
                      {/* 1. Furnizori Neplatit Report */}
                      {activeReport === "furnizori_neplatiti" && (
                        <Table hover bordered size="sm" className="align-middle rounded-3 overflow-hidden">
                          <thead className="bg-primary text-white">
                            <tr>
                              <th style={{ width: 40 }} className="text-center"></th>
                              <th>Furnizor</th>
                              <th style={{ width: 140 }} className="text-end">Nr. Facturi</th>
                              <th style={{ width: 160 }} className="text-end">Rest de Plată (RON)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {groupedSuppliers.length === 0 ? (
                              <tr>
                                <td colSpan={4} className="text-center text-muted py-5 bg-white">
                                  {loaded
                                    ? "Nu există facturi restante la data selectată."
                                    : "Alegeți parametrii și apăsați \"Generează Raport\"."}
                                </td>
                              </tr>
                            ) : (
                              groupedSuppliers.map((supp) => {
                                const isExpanded = !!expandedSuppliers[supp.contact_id];
                                return (
                                  <React.Fragment key={supp.contact_id}>
                                    <tr
                                      onClick={() => toggleSupplier(supp.contact_id)}
                                      style={{ cursor: "pointer" }}
                                      className="table-light fw-semibold"
                                    >
                                      <td className="text-center text-secondary">
                                        {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                                      </td>
                                      <td>{supp.contact_name}</td>
                                      <td className="text-end text-secondary font-monospace">
                                        {supp.invoices.length}
                                      </td>
                                      <td className="text-end text-primary font-monospace">
                                        {fmt2(supp.total_sold)}
                                      </td>
                                    </tr>

                                    {isExpanded && (
                                      <tr>
                                        <td colSpan={4} className="p-0 bg-light">
                                          <div className="p-3 border-start border-primary border-3">
                                            <Table bordered hover size="sm" className="bg-white m-0 text-secondary">
                                              <thead>
                                                <tr className="table-secondary">
                                                  <th>Număr Document</th>
                                                  <th style={{ width: 120 }} className="text-center">Data Doc.</th>
                                                  <th style={{ width: 140 }} className="text-end">Valoare</th>
                                                  <th style={{ width: 140 }} className="text-end">Plătit</th>
                                                  <th style={{ width: 140 }} className="text-end">Rest de Plată</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {supp.invoices.map((inv) => (
                                                  <tr key={inv.invoice_id}>
                                                    <td>
                                                      <span className="fw-semibold text-primary text-dark-print cursor-pointer" style={{ cursor: "pointer" }}
                                                        className="p-0 fw-semibold text-decoration-none"
                                                        onClick={() => navigate(`/intrare/${inv.invoice_id}/edit`)}
                                                      >
                                                        {inv.invoice_no}
                                                      </span>
                                                    </td>
                                                    <td className="text-center font-monospace">{inv.invoice_date}</td>
                                                    <td className="text-end font-monospace">{fmt2(inv.bxgvalue)}</td>
                                                    <td className="text-end font-monospace">{fmt2(inv.settled_value)}</td>
                                                    <td className="text-end font-monospace fw-bold text-danger">{fmt2(inv.bxsold)}</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </Table>
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                  </React.Fragment>
                                );
                              })
                            )}
                          </tbody>
                          {groupedSuppliers.length > 0 && (
                            <tfoot className="table-secondary">
                              <tr className="fw-bold">
                                <td></td>
                                <td>TOTAL GENERAL</td>
                                <td className="text-end font-monospace">
                                  {data.rows.length}
                                </td>
                                <td className="text-end font-monospace">
                                  {fmt2(groupedSuppliers.reduce((s, x) => s + x.total_sold, 0))}
                                </td>
                              </tr>
                            </tfoot>
                          )}
                        </Table>
                      )}

                      {/* 2. Intrari in Perioada Report */}
                      {activeReport === "intrari_perioada" && (
                        <Table hover bordered size="sm" className="align-middle rounded-3 overflow-hidden">
                          <thead className="bg-primary text-white">
                            <tr>
                              <th style={{ width: 110 }} className="text-center">Dată Doc.</th>
                              <th style={{ width: 150 }}>Număr Doc.</th>
                              <th>Furnizor</th>
                              <th style={{ width: 140 }} className="text-end">Valoare (bxgvalue)</th>
                              <th style={{ width: 140 }} className="text-end">Bază (RON)</th>
                              <th style={{ width: 140 }} className="text-end">TVA (RON)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.rows.length === 0 ? (
                              <tr>
                                <td colSpan={6} className="text-center text-muted py-5 bg-white">
                                  {loaded
                                    ? "Nu s-au găsit documente de intrare în perioada selectată."
                                    : "Alegeți parametrii și apăsați \"Generează Raport\"."}
                                </td>
                              </tr>
                            ) : (
                              data.rows.map((r) => (
                                <tr key={r.id}>
                                  <td className="text-center font-monospace">{r.bxdoc_date}</td>
                                  <td>
                                    <span className="fw-semibold text-primary text-dark-print cursor-pointer" style={{ cursor: "pointer" }}
                                      className="p-0 fw-semibold text-decoration-none"
                                      onClick={() => navigate(`/intrare/${r.id}/edit`)}
                                    >
                                      {r.bxdoc_no}
                                    </span>
                                  </td>
                                  <td>{r.contact_name}</td>
                                  <td className="text-end font-monospace">{fmt2(r.bxgvalue)}</td>
                                  <td className="text-end font-monospace">{fmt2(r.baza_l)}</td>
                                  <td className="text-end font-monospace">{fmt2(r.tva_l)}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                          {data.rows.length > 0 && (
                            <tfoot className="table-secondary">
                              <tr className="fw-bold">
                                <td colSpan={3} className="text-end">TOTAL</td>
                                <td className="text-end font-monospace">
                                  {fmt2(data.rows.reduce((s, x) => s + x.bxgvalue, 0))}
                                </td>
                                <td className="text-end font-monospace">
                                  {fmt2(data.rows.reduce((s, x) => s + x.baza_l, 0))}
                                </td>
                                <td className="text-end font-monospace">
                                  {fmt2(data.rows.reduce((s, x) => s + x.tva_l, 0))}
                                </td>
                              </tr>
                            </tfoot>
                          )}
                        </Table>
                      )}

                      {/* 3. Jurnal TVA Intrari Direct Taxabile */}
                      {activeReport === "tva_direct" && (
                        <Table hover bordered size="sm" className="align-middle rounded-3 overflow-hidden">
                          <thead className="bg-primary text-white">
                            <tr>
                              <th style={{ width: 110 }} className="text-center">Data</th>
                              <th style={{ width: 110 }}>Număr</th>
                              <th>Partener</th>
                              <th style={{ width: 130 }}>CUI</th>
                              <th style={{ width: 70 }} className="text-end">Cota</th>
                              <th style={{ width: 130 }} className="text-end">Bază</th>
                              <th style={{ width: 130 }} className="text-end">TVA</th>
                              <th style={{ width: 130 }} className="text-end">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {groupedTvaDirect.length === 0 ? (
                              <tr>
                                <td colSpan={8} className="text-center text-muted py-5 bg-white">
                                  {loaded
                                    ? "Nu există înregistrări pentru perioada selectată."
                                    : "Alegeți parametrii și apăsați \"Generează Raport\"."}
                                </td>
                              </tr>
                            ) : (
                              groupedTvaDirect.map(({ doc_id, rows }) => {
                                const first = rows[0] || {};
                                const subBaza = rows.reduce((s, r) => s + Number(r.baza || 0), 0);
                                const subTva = rows.reduce((s, r) => s + Number(r.tva || 0), 0);
                                const subTot = rows.reduce((s, r) => s + Number(r.total || 0), 0);

                                const span = rows.length + 1;

                                return (
                                  <React.Fragment key={doc_id}>
                                    {rows.map((r, idx) => (
                                      <tr key={`${doc_id}-${r.cota}-${idx}`}>
                                        {idx === 0 && (
                                          <>
                                            <td rowSpan={span} className="bg-white">{first.doc_date}</td>
                                            <td rowSpan={span} className="bg-white">
                                              <span className="fw-semibold text-primary text-dark-print cursor-pointer" style={{ cursor: "pointer" }}
                                                className="p-0 fw-semibold text-decoration-none"
                                                onClick={() => navigate(`/intrare/${doc_id}/edit`)}
                                              >
                                                {first.doc_no || ""}
                                              </span>
                                            </td>
                                            <td rowSpan={span} className="bg-white">{first.partener || "-"}</td>
                                            <td rowSpan={span} className="bg-white">{first.cui || ""}</td>
                                          </>
                                        )}
                                        <td className="text-end text-secondary fw-semibold">{Number(r.cota ?? 0)}%</td>
                                        <td className="text-end font-monospace">{fmt2(r.baza)}</td>
                                        <td className="text-end font-monospace">{fmt2(r.tva)}</td>
                                        <td className="text-end font-monospace">{fmt2(r.total)}</td>
                                      </tr>
                                    ))}
                                    <tr className="table-light fw-semibold">
                                      <td className="text-end bg-light small py-1">Subtotal</td>
                                      <td className="text-end bg-light small py-1 font-monospace">{fmt2(subBaza)}</td>
                                      <td className="text-end bg-light small py-1 font-monospace">{fmt2(subTva)}</td>
                                      <td className="text-end bg-light small py-1 font-monospace">{fmt2(subTot)}</td>
                                    </tr>
                                  </React.Fragment>
                                );
                              })
                            )}
                          </tbody>
                          {groupedTvaDirect.length > 0 && (
                            <tfoot className="table-secondary">
                              {(data.totals_by_cota || []).map((t) => (
                                <tr key={`tot-${t.cota}`}>
                                  <td colSpan={5} className="text-end fw-semibold">
                                    Total cotă {Number(t.cota ?? 0)}%
                                  </td>
                                  <td className="text-end fw-semibold font-monospace">{fmt2(t.baza)}</td>
                                  <td className="text-end fw-semibold font-monospace">{fmt2(t.tva)}</td>
                                  <td className="text-end fw-semibold font-monospace">{fmt2(t.total)}</td>
                                </tr>
                              ))}
                              <tr className="table-dark text-white fw-bold">
                                <td colSpan={5} className="text-end py-2">TOTAL GENERAL LEI (RON)</td>
                                <td className="text-end py-2 font-monospace">{fmt2(totalsGeneralDirect.baza)}</td>
                                <td className="text-end py-2 font-monospace">{fmt2(totalsGeneralDirect.tva)}</td>
                                <td className="text-end py-2 font-monospace">{fmt2(totalsGeneralDirect.total)}</td>
                              </tr>
                            </tfoot>
                          )}
                        </Table>
                      )}
                    </>
                  )}
                </div>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
}
